import { defineStore } from 'pinia'
import useWishlist from '~/composables/useWishlist'
import { useToast } from 'vue-toast-notification'
import '~/components/ui/Toast/index.css'

/** Braze caps event properties in size, so the attached product data stays out of event payloads. */
const withoutProductData = (items: FlattenedWishlist) => items.map(({ flatProduct: _flatProduct, ...item }) => item)

export const useWishlistStore = defineStore('wishlist', () => {
  const { legacyGuestWishlistCookie, getWishlist, createWishlist, addItemToWishlist, removeItemFromWishlist } =
    useWishlist()
  const { getProducts } = useProducts()
  const toast = useToast()
  const i18n = useNuxtApp().$i18n
  const wishlist = ref<FlattenedWishlist>([])
  const wishlistId = ref<string | null>(null)
  const initFailed = ref(false)
  /** A product request failed, unlike SFCC leaving out a deleted product. The list itself still loaded. */
  const productsFailed = ref(false)

  const count = computed(() => wishlist.value.length)

  /** Returns false when the products could not be read, leaving the items without product data. */
  const attachProducts = async (productIds: string[]): Promise<boolean> => {
    if (!productIds.length) return true
    try {
      const products = await getProducts(productIds)
      if (!Array.isArray(products)) return false
      const productsById = new Map(products.map((product) => [product.id, product]))
      wishlist.value = wishlist.value.map((item) => {
        const product = item.productId ? productsById.get(item.productId) : undefined
        return product ? { ...item, flatProduct: product } : item
      })
      return true
    } catch (error) {
      console.error('Failed to load wishlist products:', error)
      return false
    }
  }

  const productIdsOf = (items: FlattenedWishlist) =>
    items.map((item) => item.productId).filter((id): id is string => Boolean(id))

  /** Reads the customer's list, creating one if they have none. False when it could not be read. */
  const loadList = async (): Promise<boolean> => {
    // Cleared first, so a failed read after sign-in can't leave the Guest's list standing in for the account's.
    wishlistId.value = null
    try {
      // Returns an Error rather than throwing; nothing at all means no customer id yet, not a failure.
      const lists = await getWishlist()
      if (lists instanceof Error) throw lists
      if (!lists) return true

      wishlistId.value = lists[0]?.id ?? null
      if (wishlistId.value) return true
      const created = await createWishlist()
      if (created instanceof Error) throw created
      wishlistId.value = created ?? null
      return true
    } catch (error) {
      wishlist.value = []
      console.error('Failed to load wishlist:', error)
      return false
    }
  }

  /** Adds what the list is missing, one at a time: concurrent writes fail with SFCC's Concurrent Modification error. */
  const addMissing = async (listId: string, productIds: string[]) => {
    const listed = new Set(productIdsOf(wishlist.value))
    for (const productId of productIds.filter((id) => !listed.has(id))) {
      const result = await addItemToWishlist(listId, productId)
      if (!result || result instanceof Error) console.error(`Failed to merge product ${productId} into the wishlist.`)
    }
  }

  /**
   * Moves a guest's legacy cookie into their list so nothing they wishlisted is lost. A signed-in
   * customer's cookie is stale (it was merged when they signed in), so it is only deleted.
   * TODO: remove once the cookie's 30-day lifetime has passed since this shipped.
   */
  const importLegacyCookie = async () => {
    const cookie = legacyGuestWishlistCookie()
    if (!cookie.value) return
    if (!useUserStore().isLoggedIn) {
      if (!wishlistId.value) return
      // An item that fails to save is dropped rather than kept for a retry, so the cookie goes after one attempt.
      await addMissing(wishlistId.value, String(cookie.value).split('-').filter(Boolean))
    }
    cookie.value = null
  }

  const init = async () => {
    if (!(await loadList())) {
      initFailed.value = true
      return
    }
    initFailed.value = false
    await importLegacyCookie()
    productsFailed.value = !(await attachProducts(productIdsOf(wishlist.value)))
  }

  /**
   * Call straight after signing in to an existing account, while the store still holds the Guest's
   * items. Registration keeps the same customer, so it needs no merge.
   */
  const mergeWishlist = async () => {
    const guestProductIds = productIdsOf(wishlist.value)
    initFailed.value = !(await loadList())
    if (initFailed.value || !wishlistId.value) return

    await addMissing(wishlistId.value, guestProductIds)
    // The account's own items arrive without product data too, not only the merged ones.
    productsFailed.value = !(await attachProducts(productIdsOf(wishlist.value)))
  }

  const addItem = (product: FlattenedWishlistItem) => {
    const isProductInWishlist = wishlist.value.some((item) => item.productId === product.productId)

    if (isProductInWishlist) {
      console.warn(`Product with ID ${product.productId} is already in the wishlist.`)
      return
    }

    wishlist.value.push(product)
  }

  const removeItem = (itemId: string) => {
    if (!itemId) return

    const productIdToRemove = wishlist.value.find(
      (item: FlattenedWishlistItem) => item.listItemId === itemId,
    )?.productId

    wishlist.value = wishlist.value.filter((item: FlattenedWishlistItem) => item.listItemId !== itemId)

    toast.default(i18n.t('actions.toast_modal_remove_from_wishlist'), { duration: 3000, position: 'top' })

    // Braze integration - Remove from Wishlist Event
    useBrazeTracking().logCustomEvent('remove_from_wishlist', {
      product_id: productIdToRemove,
      wishlist_products: withoutProductData(wishlist.value),
    })
  }

  const reset = () => {
    wishlist.value = []
    wishlistId.value = null
    productsFailed.value = false
  }

  const isInWishlist = (itemId: string) => {
    return wishlist.value.some((wishlistItem) => wishlistItem.productId === itemId)
  }

  const toggleItem = async (productId: string) => {
    // Decided before waiting: a click during the layout's first load means what the shopper saw then.
    const add = !isInWishlist(productId)
    try {
      // The wishlist id only exists once the layout's first load has finished.
      await whenLayoutStoresSettled()
      if (!wishlistId.value) {
        return
      }
      if (!add) {
        const itemToRemove = wishlist.value.find((item) => item.productId === productId)
        if (itemToRemove?.listItemId) {
          await removeItemFromWishlist(wishlistId.value, itemToRemove.listItemId)
        }
      } else if (!isInWishlist(productId)) {
        const added = await addItemToWishlist(wishlistId.value, productId)
        // Returns the error rather than throwing it.
        if (!added || added instanceof Error) throw added ?? new Error('Wishlist item was not added')
        toast.success(i18n.t('actions.toast_modal_add_to_wishlist'), { duration: 3000, position: 'top' })
        // The event can trigger an IAM, so its wishlist rules must see the new item's product data.
        if (!(await attachProducts([productId]))) productsFailed.value = true
        useBrazeTracking().logCustomEvent('add_to_wishlist', {
          product_id: added.productId,
          product_quantity: added.quantity,
          product_priority: added.priority,
          wishlist_products: withoutProductData(wishlist.value),
        })
      }
    } catch (error) {
      toast.error('Failed to add/remove item from wishlist', { duration: 3000, position: 'top' })
      console.error('Failed to add/remove item from wishlist:', error)
    }
  }

  return {
    wishlist,
    count,
    initFailed,
    productsFailed,
    init,
    mergeWishlist,
    addItem,
    removeItem,
    reset,
    isInWishlist,
    toggleItem,
  }
})
